import { BulkCreateRestaurantEntryPayload } from "src/data/admin/types/restaurant";
import { parseHoursStringToScheduleEntries } from "src/data/restaurant/mappers/restaurantMapper";

const parseBool = (val: string): boolean => val.trim().toLowerCase() === "true" || val.trim() === "1";

const splitCsvRow = (row: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (ch === '"') {
            inQuotes = !inQuotes;
        } else if (ch === "," && !inQuotes) {
            result.push(current.trim());
            current = "";
        } else {
            current += ch;
        }
    }
    result.push(current.trim());
    return result;
};

export type CsvParseResult =
    | { ok: true; entries: BulkCreateRestaurantEntryPayload[] }
    | { ok: false; error: string };

export const parseCsv = (raw: string): CsvParseResult => {
    const lines = raw.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) return { ok: false, error: "CSV must have a header row and at least one data row." };

    const headers = splitCsvRow(lines[0]);
    // "hours" is a CSV-only column (legacy string format) that gets parsed into `schedule`
    const required: string[] = [
        "name", "cityName", "quartier", "price", "phone", "address",
        "latitude", "longitude", "hours", "bookingDuration", "paymentMethods",
        "numberOfSeats", "live", "servesAlcohol", "featured", "googleRating",
        "hasAnimation", "instaTag", "bookingEnabled", "tags",
    ];

    const missing = required.filter((f) => !headers.includes(f));
    if (missing.length > 0) return { ok: false, error: `Missing CSV columns: ${missing.join(", ")}` };

    const entries: BulkCreateRestaurantEntryPayload[] = [];

    for (let i = 1; i < lines.length; i++) {
        const cols = splitCsvRow(lines[i]);
        const row: Record<string, string> = {};
        headers.forEach((h, idx) => { row[h] = (cols[idx] ?? "").trim(); });

        entries.push({
            name: row.name,
            cityName: row.cityName,
            quartier: row.quartier,
            price: row.price as BulkCreateRestaurantEntryPayload["price"],
            phone: row.phone,
            address: row.address,
            latitude: parseFloat(row.latitude),
            longitude: parseFloat(row.longitude),
            schedule: parseHoursStringToScheduleEntries(row.hours),
            bookingDuration: parseInt(row.bookingDuration, 10),
            paymentMethods: row.paymentMethods as BulkCreateRestaurantEntryPayload["paymentMethods"],
            numberOfSeats: parseInt(row.numberOfSeats, 10),
            live: parseBool(row.live),
            servesAlcohol: parseBool(row.servesAlcohol),
            featured: parseBool(row.featured),
            googleRating: parseFloat(row.googleRating),
            hasAnimation: parseBool(row.hasAnimation),
            instaTag: row.instaTag,
            bookingEnabled: parseBool(row.bookingEnabled),
            tags: row.tags ? row.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        });
    }

    return { ok: true, entries };
};
